import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { decidiMenu, fasciaCheRitaglia, TETTO } from '../../utils/spazio-tendina';
import '../shared/sweep-ring.css';
import './VariantSelect.css';

interface VariantSelectProps {
  value: string;
  onChange: (variant: string) => void;
  variants: readonly string[];
  /** Variants the user has actually played — surfaced at the top of the list. */
  played?: string[];
  /**
   * The user's preferred variant — floated to the very top of the list (above
   * the played variants), so it is always easy to find. Ignored if unset or not
   * present in `variants`.
   */
  preferred?: string;
  /** Resolve a variant key to its display label. */
  label: (variant: string) => string;
  /** Resolve a variant key to its accent dot colour. */
  dotColor: (variant: string) => string;
  /** Render the accent dot beside each variant. Defaults to true. */
  showDot?: boolean;
  /**
   * Render a custom leading element (e.g. a flag emoji) in place of the accent
   * dot, in both the trigger and the menu options. Takes precedence over showDot.
   */
  renderLeading?: (variant: string) => ReactNode;
  /**
   * Render a custom trailing element after an option's label (e.g. a count).
   * MENU ONLY — the trigger stays a bare label, where horizontal room is
   * scarcest. Return null for options that have nothing to show.
   */
  renderTrailing?: (variant: string) => ReactNode;
  /**
   * Etichetta del SOLO pulsante, quando deve essere piu' corta di quella del
   * menu: la pill fra i tag del problema del giorno mostra "ITA", il menu
   * "Italiana 8x8". Se non c'e', il pulsante usa `label` come sempre.
   */
  triggerLabel?: (variant: string) => ReactNode;
  /**
   * Accende l'anello di luce verde che percorre il bordo del pulsante — lo
   * stesso della pillola «N giocatori collegati» (`.sweep-ring`). Serve dove il
   * pulsante non sembra un pulsante: la pill della variante nella card del
   * problema, vestita da tag, che senza questo non dice di potersi premere.
   * Spento di default: dove il trigger e' gia' un campo a tutta larghezza
   * l'anello sarebbe rumore.
   */
  sweep?: boolean;
  ariaLabel?: string;
  /**
   * L'id della nota che spiega la scelta, letta dietro all'etichetta.
   *
   * Serve dove la nota non sta piu' nello stesso riquadro del comando: nel
   * modale dei tornei i posti e il minimo stanno affiancati e i loro avvisi
   * sono stati portati a tutta larghezza sotto la coppia, quindi il legame
   * «questo avviso parla di QUEL campo» non passa piu' dalla vicinanza.
   */
  ariaDescribedBy?: string;
}

/**
 * Custom glass dropdown for picking a variant. Replaces the native <select> so
 * the open menu matches the editorial design (glass popover, colored dots,
 * played-variants pinned on top). Keyboard accessible: Arrow/Home/End to move,
 * Enter/Space to select, Escape to close, click-outside to dismiss.
 */
export default function VariantSelect({
  value,
  onChange,
  variants,
  played = [],
  preferred,
  label,
  dotColor,
  showDot = true,
  renderLeading,
  renderTrailing,
  triggerLabel,
  sweep = false,
  ariaLabel,
  ariaDescribedBy,
}: VariantSelectProps) {
  const [open, setOpen] = useState(false);
  // Menu opens above the trigger when there isn't enough room below (e.g. a
  // dropdown near the bottom of a full-screen mobile sheet).
  const [openUp, setOpenUp] = useState(false);
  /*
   * L'altezza massima del menu, decisa all'apertura sullo spazio che c'e'
   * DAVVERO. Quando non basta, il menu scorre dentro di se' (`overflow-y: auto`
   * ce l'ha gia' dal foglio) invece di uscire dal suo riquadro e farsi
   * mozzare. Parte dal tetto del foglio, cosi' il primo disegno non e' mai
   * piu' alto di quello che il CSS permette.
   */
  const [altezzaMassima, setAltezzaMassima] = useState<number>(TETTO);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Pinned group first (preferred floated to the very top, then played variants),
  // preserving canonical order within each group, then the rest.
  const playedSet = new Set(played.filter((v) => variants.includes(v)));
  const hasPreferred = preferred !== undefined && variants.includes(preferred);
  const pinned = [
    ...(hasPreferred ? [preferred as string] : []),
    ...variants.filter((v) => v !== preferred && playedSet.has(v)),
  ];
  const ordered = [...pinned, ...variants.filter((v) => v !== preferred && !playedSet.has(v))];
  const dividerAfter = pinned.length > 0 && pinned.length < ordered.length ? pinned.length - 1 : -1;

  // Leading element before each variant label: a custom render (e.g. a flag)
  // takes precedence, else the accent dot, else nothing.
  const leading = (variant: string): ReactNode =>
    renderLeading
      ? renderLeading(variant)
      : showDot
        ? <span className="variant-dot-mini" aria-hidden="true" style={{ background: dotColor(variant) }} />
        : null;

  const close = useCallback(() => setOpen(false), []);

  const openMenu = () => {
    setHighlight(Math.max(0, ordered.indexOf(value)));
    /*
     * Verso e altezza si decidono sul riquadro CHE RITAGLIA DAVVERO, non sullo
     * schermo: dentro un foglio a tutto schermo il corpo che scorre finisce
     * sopra il bordo della finestra, perche' sotto c'e' la barra appiccicata
     * con l'invio. Misurando lo schermo il menu si credeva libero, si apriva in
     * giu' e veniva mozzato — le sei voci di «Inizia Tra» ne mostravano due.
     * Il conto, coi numeri misurati, sta in utils/spazio-tendina.ts.
     *
     * In jsdom i rettangoli sono zeri e nessun antenato dichiara `overflow`:
     * si ricade sulla finestra e il menu si apre in giu' alto quanto il tetto
     * del foglio, cioe' come prima.
     */
    const radice = rootRef.current;
    const rect = radice?.getBoundingClientRect();
    if (radice && rect) {
      const { su, altezzaMassima: altezza } = decidiMenu(
        rect,
        fasciaCheRitaglia(radice),
        ordered.length,
      );
      setOpenUp(su);
      setAltezzaMassima(altezza);
    }
    setOpen(true);
  };

  const select = (variant: string) => {
    onChange(variant);
    setOpen(false);
  };

  // Dismiss on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouse = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDocMouse);
    return () => document.removeEventListener('mousedown', onDocMouse);
  }, [open, close]);

  // Move focus into the menu when it opens.
  useEffect(() => {
    if (open) menuRef.current?.focus();
  }, [open]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!open) return;
    const el = menuRef.current?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight, open]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((h) => Math.min(ordered.length - 1, h + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
        break;
      case 'Home':
        e.preventDefault();
        setHighlight(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlight(ordered.length - 1);
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const v = ordered[highlight];
        if (v) select(v);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="variant-select" ref={rootRef}>
      <button
        type="button"
        className="variant-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        {...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            openMenu();
          }
        }}
      >
        {/* Elemento vero e non pseudo-elemento: maschera e gradiente rotante
            devono stare su due layer separati. Vedi shared/sweep-ring.css. */}
        {sweep && <span className="sweep-ring" aria-hidden="true" />}
        {leading(value)}
        <span className="variant-select-value">{(triggerLabel ?? label)(value)}</span>
        <ChevronDown size={16} className={`variant-select-chevron${open ? ' open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`variant-select-menu${openUp ? ' up' : ''}`}
          /* In linea e non nel foglio: e' un numero MISURATO all'apertura, e
             cambia da una tendina all'altra e da uno schermo all'altro. */
          style={{ maxHeight: altezzaMassima }}
          role="listbox"
          tabIndex={-1}
          ref={menuRef}
          aria-label={ariaLabel}
          onKeyDown={onMenuKeyDown}
        >
          {ordered.map((v, i) => (
            <Fragment key={v}>
              <button
                type="button"
                role="option"
                aria-selected={v === value}
                data-idx={i}
                className={`variant-select-option${v === value ? ' selected' : ''}${i === highlight ? ' active' : ''}`}
                onClick={() => select(v)}
                onMouseEnter={() => setHighlight(i)}
              >
                {leading(v)}
                <span className="variant-select-option-label">{label(v)}</span>
                {renderTrailing?.(v)}
                {v === value && <Check size={14} className="variant-select-check" aria-hidden="true" />}
              </button>
              {i === dividerAfter && <div className="variant-select-divider" role="separator" />}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
